import { useEffect, useState } from "react";
import { Package } from "lucide-react";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { fetchInsurerPackages, type InsurancePackage } from "../api/claimsApi";
import { BenefitPlanTable } from "../components/BenefitPlanTable";
import { KpiCard, KpiGrid, type KpiTone } from "../components/KpiCard";

const PACKAGE_TONES: KpiTone[] = ["sky", "mint", "purple", "amber", "pink", "lime", "slate"];

function money(value: number | null, currency: string): string {
  if (value === null) return "—";
  return `${value.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}`;
}

/**
 * Compte Assurance (consultation) : les packages de l'assureur en cartes ; un clic affiche le plan de couverture
 * du package.
 */
export function InsurerCoveragePage() {
  const [packages, setPackages] = useState<InsurancePackage[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);

  useEffect(() => {
    fetchInsurerPackages()
      .then((packageRows) => {
        setPackages(packageRows);
        setSelectedId((current) => current ?? packageRows[0]?.id ?? null);
      })
      .catch((err) => setError(err instanceof Error ? err.message : "Chargement impossible."));
  }, []);

  return (
    <>
      <Breadcrumbs items={[{ label: "Couverture" }]} />
      {error ? <p className="mb-4 text-sm text-red-600">{error}</p> : null}

      <div className="flex flex-col gap-6">
        <section className="flex flex-col gap-3">
          <h1 className="text-lg font-semibold text-foreground">Packages de l'assureur ({packages.length})</h1>
          {packages.length === 0 && !error ? <p className="text-sm text-muted">Aucun package enregistré.</p> : null}
          <KpiGrid className="grid-cols-1 sm:grid-cols-2 xl:grid-cols-4">
            {packages.map((pkg, index) => (
              <KpiCard
                key={pkg.id}
                tone={PACKAGE_TONES[index % PACKAGE_TONES.length]}
                icon={Package}
                label={`${pkg.name} (${pkg.code})`}
                value={pkg.members_count}
                unit={pkg.members_count > 1 ? "assurés" : "assuré"}
                hint={`Plafond ${money(pkg.person_ceiling, pkg.currency)} par personne · ${pkg.sections.length} section(s)${pkg.status === "inactive" ? " · inactif" : ""}${
                  // Assurés = agents couverts par ce package.
                  pkg.members_count === 0 ? " · aucun agent affecté" : ""
                }`}
                onClick={() => setSelectedId(pkg.id)}
                active={pkg.id === selectedId}
              />
            ))}
          </KpiGrid>
        </section>

        {packages
          .filter((pkg) => pkg.id === selectedId)
          .map((pkg) => (
            <section key={pkg.id} className="theme-card flex flex-col gap-3 p-5">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <h2 className="text-lg font-semibold text-foreground">
                  Plan de couverture {pkg.name} <span className="text-xs font-normal text-muted">({pkg.code})</span>
                </h2>
                <p className="text-sm text-muted">
                  Plafond {money(pkg.person_ceiling, pkg.currency)} par personne
                  {pkg.family_ceiling !== null ? ` · ${money(pkg.family_ceiling, pkg.currency)} par famille` : ""}
                  {pkg.coverage_zone ? ` · zone : ${pkg.coverage_zone}` : ""}
                </p>
              </div>
              {pkg.description ? <p className="text-sm text-muted">{pkg.description}</p> : null}
              <BenefitPlanTable plan={pkg} />
            </section>
          ))}
      </div>
    </>
  );
}
